import type {
  Attribute,
  OrdinaryAttribute,
  RestraintBreakdown,
  RestraintInput,
} from "../types";

export const UPPER_ATTRIBUTES: OrdinaryAttribute[] = ["炎", "冰", "雷"];
export const LOWER_ATTRIBUTES: OrdinaryAttribute[] = ["光", "暗", "幽"];

const BASE_MODIFIERS = new Map<string, number>([
  ["炎>雷", 0.3],
  ["雷>冰", 0.3],
  ["冰>炎", 0.3],
  ["雷>炎", -0.25],
  ["冰>雷", -0.25],
  ["炎>冰", -0.25],
  ["光>暗", 0.3],
  ["暗>幽", 0.25],
  ["幽>光", 0.25],
  ["暗>光", -0.25],
  ["幽>暗", -0.2],
  ["光>幽", -0.2],
]);

for (const attribute of UPPER_ATTRIBUTES) {
  BASE_MODIFIERS.set(`神>${attribute}`, 0.15);
  BASE_MODIFIERS.set(`${attribute}>神`, -0.2);
  BASE_MODIFIERS.set(`${attribute}>魔`, 0.2);
  BASE_MODIFIERS.set(`魔>${attribute}`, -0.2);
}

for (const attribute of LOWER_ATTRIBUTES) {
  BASE_MODIFIERS.set(`魔>${attribute}`, 0.15);
  BASE_MODIFIERS.set(`${attribute}>魔`, -0.2);
  BASE_MODIFIERS.set(`${attribute}>神`, 0.2);
  BASE_MODIFIERS.set(`神>${attribute}`, -0.2);
}

export function getBaseModifier(attacker: Attribute, defender: Attribute): number {
  return BASE_MODIFIERS.get(`${attacker}>${defender}`) ?? 0;
}

export function getAttackSoulOptions(attribute: Attribute): OrdinaryAttribute[] {
  if (attribute === "神") return UPPER_ATTRIBUTES;
  if (attribute === "魔") return LOWER_ATTRIBUTES;
  return [];
}

export function getDefenseSoulOptions(attribute: Attribute): OrdinaryAttribute[] {
  if (attribute === "神") return LOWER_ATTRIBUTES;
  if (attribute === "魔") return UPPER_ATTRIBUTES;
  return [];
}

export function resolveRestraint(input: RestraintInput): RestraintBreakdown {
  const baseModifier = getBaseModifier(input.attacker, input.defender);
  const attackSoulOptions = getAttackSoulOptions(input.attacker);
  const defenseSoulOptions = getDefenseSoulOptions(input.defender);
  const attackSoulBonus =
    input.attackSoulChain &&
    attackSoulOptions.includes(input.attackSoulChain) &&
    input.attackSoulChain === input.defender
      ? 0.2
      : 0;
  const defenseSoulReduction =
    input.defenseSoulChain &&
    defenseSoulOptions.includes(input.defenseSoulChain) &&
    input.defenseSoulChain === input.attacker
      ? 0.2
      : 0;
  const totalModifier =
    baseModifier +
    input.manualExtra +
    attackSoulBonus -
    input.manualReduction -
    defenseSoulReduction;
  const effectiveMultiplier = 1 + totalModifier;
  const epsilon = 1e-10;
  const tone =
    effectiveMultiplier > 1 + epsilon
      ? "advantage"
      : effectiveMultiplier < 1 - epsilon
        ? "disadvantage"
        : "neutral";
  const label =
    tone === "advantage"
      ? `克制 +${formatPercent(totalModifier)}`
      : tone === "disadvantage"
        ? `被克制 ${formatPercent(totalModifier)}`
        : "无克制关系";

  return {
    baseModifier,
    manualExtra: input.manualExtra,
    attackSoulBonus,
    manualReduction: input.manualReduction,
    defenseSoulReduction,
    totalModifier,
    effectiveMultiplier,
    label,
    tone,
  };
}

function formatPercent(value: number): string {
  const percent = value * 100;
  return `${Number(percent.toFixed(8))}%`;
}

